import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import MailOutline from '@mui/icons-material/MailOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { BUTTON_LOADING_ICON } from '../../component/BottomButtonGroup'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'
import {
    APP_MARKETING_EXCEL_UPLOAD_LEAVE_MESSAGE,
    usePendingAppMarketingExcelUploadUnloadGuard,
} from './appMarketingLeaveGuard'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
    },
}

const APP_MARKETING_ACTION = {
    DELETE: 'delete',
    UPLOAD: 'upload',
    EMAIL: 'email',
}

const EXCEL_FILE_ACCEPT = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
const EXCEL_FILE_ERROR_MESSAGE = '엑셀 파일만 업로드할 수 있습니다.'

const APP_MARKETING_TABLE_COLUMNS = [
    { width: 70 },
    { width: 260 },
    { width: 280 },
    { width: 110 },
    { width: 180 },
    { width: 180 },
    { width: 120 },
]

const APP_MARKETING_TABLE_MIN_WIDTH = APP_MARKETING_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_MARKETING_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_MARKETING_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

// 마케팅 기업 일괄 등록은 기존 운영 기준인 xlsx/xls 엑셀 파일만 통과시킨다.
const isExcelFile = (file) => {
    return Boolean(file) && (
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        || file.type === 'application/vnd.ms-excel'
        || /\.(xlsx|xls)$/i.test(file.name || '')
    )
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

export default function AppMarketings() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [listLoading, setListLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const silentListRefreshRef = useRef(false)
    const deletingMarketingRef = useRef(false)
    const uploadingExcelRef = useRef(false)
    const sendingEmailRef = useRef(false)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const isActionLoading = Boolean(actionLoading)
    const isExcelUploading = actionLoading?.type === APP_MARKETING_ACTION.UPLOAD
    const isEmailSending = actionLoading?.type === APP_MARKETING_ACTION.EMAIL
    const leaveGuardEnabled = isExcelUploading

    usePendingAppMarketingExcelUploadUnloadGuard(leaveGuardEnabled)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setListLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appMarketing/getList', { page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                if (showSpinner) {
                    setListLoading(false)
                }
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '마케팅 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '마케팅 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        }

        if (showSpinner) {
            setListLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 같은 렌더 주기에 여러 액션이 눌려도 ref 기준으로 즉시 중복 실행을 막는다.
    const hasActionInProgress = () => isActionLoading || deletingMarketingRef.current || uploadingExcelRef.current || sendingEmailRef.current

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (hasActionInProgress()) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect((prevSearchSelect) => ({
            ...prevSearchSelect,
            [name]: value
        }))
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (hasActionInProgress()) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (hasActionInProgress()) {
            return
        }

        if (curPage !== 1) {
            setCurPage(1)
        }

        const trimmedSearchText = (searchText || '').trim()

        if (searchQuery !== trimmedSearchText) {
            setSearchQuery(trimmedSearchText)
        } else if (curPage === 1) {
            getData(false)
        }
    }

    const hasActiveFilters = Boolean(
        searchQuery ||
        searchSelect.searchStatus !== DEFAULT_SEARCH_SELECT.searchStatus
    )
    const excelUploadButtonLabel = isExcelUploading ? '엑셀 업로드 중...' : '기업정보등록(엑셀파일)'
    const emailButtonLabel = isEmailSending ? '이메일 발송 중...' : '이메일발송(100건)'

    // 목록 상태에 맞는 빈 화면을 한 곳에서 결정한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="마케팅 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button variant="outlined" size="small" onClick={() => getData(false)} disabled={isActionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 마케팅 작업 기업이 없습니다."
                    description="기업명 검색어와 메일발송여부 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isActionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 마케팅 작업 기업이 없습니다."
                description="마케팅 작업 기업을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const handleDelete = (id) => {
        if (hasActionInProgress()) {
            return
        }

        const deleteData = async () => {
            deletingMarketingRef.current = true
            setActionLoading({ type: APP_MARKETING_ACTION.DELETE, id })
            try {
                const { data } = await API.post('/api/v1/admin/appMarketing/delete', { marketingId: id })

                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)
                    if (curPage > 1 && params.length === 1) {
                        setCurPage(curPage - 1)
                    } else {
                        getData(false)
                    }
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('마케팅 작업 기업을 삭제하지 못했습니다.')
            } finally {
                deletingMarketingRef.current = false
                setActionLoading(null)
            }
        }
        deleteData()
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const input = e.target

        if (hasActionInProgress()) {
            input.value = ''
            return
        }

        const file = input.files[0]

        if (!file) {
            return
        }

        if (uploadingExcelRef.current) {
            input.value = ''
            return
        }

        if (!isExcelFile(file)) {
            input.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(EXCEL_FILE_ERROR_MESSAGE)
            return
        }

        uploadingExcelRef.current = true
        createExcel(input, file)
    }

    const createExcel = async (input, file) => {
        const formData = new FormData()
        formData.append('file', file)

        setActionLoading({ type: APP_MARKETING_ACTION.UPLOAD })

        try {
            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appMarketing/createExcel',
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message + data.data)
                getData(false)
            } else if (data.code === 403) {
                setTimeout(() => {
                    history.push('/login')
                }, 0)
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('마케팅 작업 기업 엑셀 등록에 실패했습니다.')
        } finally {
            uploadingExcelRef.current = false
            input.value = '' // 성공/실패와 무관하게 같은 파일 재선택 가능하도록 초기화
            setActionLoading(null)
        }
    }

    const sendEmail = async () => {
        if (hasActionInProgress()) {
            return
        }

        sendingEmailRef.current = true
        setActionLoading({ type: APP_MARKETING_ACTION.EMAIL })
        try {
            const { data } = await API.post('/api/v1/admin/appMarketing/sendEmail')

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message + data.data)
                getData(false)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('마케팅 작업 기업 이메일 발송에 실패했습니다.')
        } finally {
            sendingEmailRef.current = false
            setActionLoading(null)
        }
    }

    return (
        <Grid item xs={12}>
            <Prompt when={leaveGuardEnabled} message={APP_MARKETING_EXCEL_UPLOAD_LEAVE_MESSAGE} />
            {listLoading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>마케팅 작업 기업 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isActionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="기업명"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isActionLoading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isActionLoading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="메일발송여부"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                                disabled={isActionLoading}
                            >
                                <MenuItem value=''>메일발송여부</MenuItem>
                                <MenuItem value={0}>미발송</MenuItem>
                                <MenuItem value={1}>발송</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        left={(
                            <>
                                <FormControl>
                                    <TextField
                                        inputProps={{ accept: EXCEL_FILE_ACCEPT }}
                                        style={{ display: 'none' }}
                                        name="excelFile"
                                        id="excelFile"
                                        type="file"
                                        disabled={isActionLoading}
                                        onChange={e => handleUploadClick(e)}
                                    />
                                    <Box component="label" htmlFor="excelFile" sx={{ display: 'inline-flex' }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            component="span"
                                            startIcon={isExcelUploading ? BUTTON_LOADING_ICON : <UploadFileIcon />}
                                            aria-busy={isExcelUploading}
                                            size="small"
                                            disabled={isActionLoading}
                                            sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                        >
                                            {excelUploadButtonLabel}
                                        </Button>
                                    </Box>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    onClick={sendEmail}
                                    startIcon={isEmailSending ? BUTTON_LOADING_ICON : <MailOutline />}
                                    aria-busy={isEmailSending}
                                    disabled={isActionLoading}
                                    size="small"
                                    sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                >
                                    {emailButtonLabel}
                                </Button>
                            </>
                        )}
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isActionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appMarketing',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer sx={{ opacity: isActionLoading ? 0.6 : 1, pointerEvents: isActionLoading ? 'none' : 'auto' }}>
                    <Table size="small" sx={{ minWidth: APP_MARKETING_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_MARKETING_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-marketing-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>기업명</TableCell>
                                <TableCell>이메일</TableCell>
                                <TableCell>발송여부</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell>발송일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.marketingId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell sx={APP_MARKETING_TEXT_CELL_SX}>{row.companyName || '-'}</TableCell>
                                        <TableCell sx={APP_MARKETING_TEXT_CELL_SX}>{row.email || '-'}</TableCell>
                                        <TableCell sx={APP_MARKETING_LABEL_CELL_SX}>{row.isEmailSend === 1 ? '발송' : '-'}</TableCell>
                                        <TableCell sx={APP_MARKETING_LABEL_CELL_SX}>{renderDateTime(row.regTime)}</TableCell>
                                        <TableCell sx={APP_MARKETING_LABEL_CELL_SX}>{renderDateTime(row.updTime)}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup disabled={isActionLoading}>
                                                <EditIcon path={'appMarketing'} id={row.marketingId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={isActionLoading}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.marketingId} disabled={isActionLoading}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    <Box sx={{ opacity: isActionLoading ? 0.6 : 1, pointerEvents: isActionLoading ? 'none' : 'auto' }}>
                        {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
